import React from "react";
import { PageLayout, Clause, Callout, Bullets } from "@/components/PageLayout";

export default function PrivacyPolicyEn() {
  return (
    <PageLayout
      testId="privacy-policy-page"
      eyebrow="Legal"
      title="Privacy Policy"
      updated="1 June 2026"
      intro="Seleksa is built on a deliberately minimal data footprint. This policy explains exactly what we hold, what we never touch, and how responsibility is divided between Seleksa and the photography studio you are working with."
    >
      <Clause n={1} title="The short version">
        <Bullets items={[
          "Your original high-resolution RAW and JPG files never reach our servers.",
          "We store only compressed, web-resolution preview thumbnails needed to display a gallery in a browser.",
          "File sorting happens on the studio's own computer, using scripts we generate for them.",
          "The studio owns the client relationship and the data; Seleksa only processes it on their instruction.",
          "We never sell personal data, and we never use gallery photographs to train machine-learning models.",
        ]} />
      </Clause>

      <Clause n={2} title="Zero-Server-Load architecture">
        <p>
          Most gallery platforms require a studio to upload an entire shoot before a client can look at it. Seleksa
          does not work that way. A studio keeps its shoot where it already lives — typically a Google Drive folder
          they control — and Seleksa references it.
        </p>
        <Callout>
          Seleksa does not receive, process, transcode, archive or back up your original high-resolution RAW or JPG
          files. They remain in the studio's own storage at all times.
        </Callout>
        <p>
          What a client's browser loads is a compressed, web-resolution proxy image, served for display purposes only.
          These previews exist so a client can view and choose photographs on a phone or laptop without downloading
          gigabytes of data. They are unsuitable for printing or redistribution, and they are the only image data
          associated with your gallery inside our system.
        </p>
        <p>
          The practical consequences are deliberate: there is no large-scale library of your original work sitting on
          infrastructure you do not control, no upload wait before a client can start choosing, and a materially
          smaller blast radius if any single component were ever compromised.
        </p>
      </Clause>

      <Clause n={3} title="Local file sorting — nothing is transmitted">
        <p>
          When a client has finished choosing, Seleksa generates a small automation script (a <code>.bat</code> file on
          Windows or a <code>.sh</code> file on macOS and Linux) which the studio downloads and runs on their own
          machine. The script reads the studio's local folders, then copies or moves the selected photographs into a
          tidy output structure.
        </p>
        <Callout>
          These scripts run entirely on the studio's hardware. They do not upload, transmit or report any file content
          back to Seleksa or to any third party. We never see the files they touch.
        </Callout>
        <p>
          The script contains only the filenames the client selected and the destination folder names. Studios should
          review any script before running it, and are responsible for its execution on their own equipment — see our
          Terms of Service.
        </p>
      </Clause>

      <Clause n={4} title="Controller and Processor: who is responsible for what">
        <p>
          Seleksa operates a business-to-business-to-consumer service. A photography studio subscribes to Seleksa, and
          that studio's clients then use the galleries the studio creates. This means two distinct roles exist, and the
          distinction matters legally.
        </p>
        <Bullets items={[
          "The Studio is the Data Controller. They decide which clients to invite, which photographs to show, what personal data to enter, and how long to keep working with that client. They are responsible for having a lawful basis to process their client's data and for answering their client's privacy requests.",
          "Seleksa is the Data Processor. We store and process that data solely to provide the service the studio has asked for. We act on the studio's instructions and do not use their client data for our own independent purposes.",
        ]} />
        <p>
          If you are a photography client and want your data corrected or erased, please contact the studio that sent
          you your gallery link — they control that record. If you contact us directly, we will refer you to the
          relevant studio, since acting unilaterally on their data would breach our agreement with them.
        </p>
      </Clause>

      <Clause n={5} title="What we actually store">
        <p>For a subscribing studio (our direct customer):</p>
        <Bullets items={[
          "Account details: studio name, owner and staff names, email addresses, hashed passwords, and a Google account identifier where Google sign-in is used.",
          "Commercial records: subscription tier, payment history and transaction references from our payment processor, invoices, and any promotional code applied.",
          "Configuration: price catalogues, print sizes, project templates, and studio branding such as an uploaded logo.",
        ]} />
        <p>For a photography client using a gallery (our customer's customer):</p>
        <Bullets items={[
          "Identity and contact details entered by the studio: name, email address and WhatsApp number.",
          "Selection data: which photographs were chosen, which were marked as extra, the print sizes and frame codes requested, and any written notes or revision requests submitted.",
          "Gallery access records: the private share token, an access code where issued, and timestamps of when the gallery was opened and submitted.",
          "Compressed web-resolution preview images of the photographs in that gallery.",
        ]} />
        <p>
          We never ask for, and have no use for, payment card numbers from photography clients. Any money a client owes
          a studio is settled directly between them, outside Seleksa.
        </p>
      </Clause>

      <Clause n={6} title="Payments">
        <p>
          Studio subscription payments are processed by Midtrans, a licensed Indonesian payment gateway, or — for
          studios outside Indonesia — by PayPal. Card numbers,
          bank credentials and e-wallet authentication are handled inside Midtrans's or PayPal's environment and are never
          transmitted to, or stored by, Seleksa. We retain only the transaction identifier, amount, method and status
          needed to reconcile a subscription and issue a receipt.
        </p>
      </Clause>

      <Clause n={7} title="Email and messaging">
        <p>
          We send transactional email — gallery invitations, selection summaries, reminders, and subscription notices —
          through a third-party email delivery provider acting as our sub-processor. WhatsApp messages are not sent by
          our servers: Seleksa prepares a pre-filled message and opens it in the studio's own WhatsApp account, so the
          studio sends it under their own identity.
        </p>
        <p>
          We will never email you asking for your password, a verification code, or payment details. Any message that
          does is not from us.
        </p>
      </Clause>

      <Clause n={8} title="Cookies and sessions">
        <p>
          When you sign in, we set a single essential session cookie. It is marked <code>httpOnly</code>, meaning
          JavaScript running in the page cannot read it — a deliberate defence against session theft through
          cross-site scripting. We also store your language preference locally in your browser.
        </p>
        <p>
          We do not use advertising cookies, cross-site trackers, or third-party analytics that profile you across
          other websites.
        </p>
      </Clause>

      <Clause n={9} title="Retention and deletion">
        <p>
          Gallery data is subject to an automatic retention window. Once a project passes the end of that window, a
          scheduled process clears its preview images and marks the project expired, while retaining a minimal
          administrative record — project title, dates and totals — so studios keep an accurate history of the work
          they have invoiced.
        </p>
        <p>
          A studio may delete a project at any time from their dashboard. On subscription cancellation, studio account
          data is retained only for the period required to meet Indonesian tax and accounting obligations, then
          removed.
        </p>
      </Clause>

      <Clause n={10} title="Security">
        <p>
          Access inside Seleksa is scoped by studio: every database query is bound to the requesting studio's
          identifier, so one studio cannot read another's projects, clients or revenue. Roles further restrict what
          each account can do, with financial figures visible only to a studio owner. Passwords are stored as salted
          one-way hashes and cannot be recovered by us or by anyone else.
        </p>
        <p>
          Gallery links contain a long, unguessable token, and studios can additionally issue a per-client access code.
          Anyone holding a gallery link can open that gallery, so studios should share links only with the intended
          client and reset the access code if a link is exposed.
        </p>
        <p>
          No system is perfectly secure. If we discover a breach affecting personal data, we will notify affected
          studios without undue delay and provide the detail they need to fulfil their own obligations as Controller.
        </p>
      </Clause>

      <Clause n={11} title="Children">
        <p>
          Seleksa is a business tool and is not directed at children. Graduation, family and school portrait sessions
          frequently include minors as photographic subjects; where that is the case, the studio is responsible for
          obtaining consent from a parent or guardian before entering their details or sharing a gallery.
        </p>
      </Clause>

      <Clause n={12} title="Changes and contact">
        <p>
          We will update this policy as the service develops, and will revise the date at the top when we do. Material
          changes affecting subscribing studios will be communicated by email.
        </p>
        <p>
          Questions about this policy, or about a specific data request, can be sent to{" "}
          <a href="mailto:seleksa.com@gmail.com" className="text-[#D4AF37] hover:underline">seleksa.com@gmail.com</a>,
          or posted to Jl. Pasar 1, Komp SBE (Setia Budi Estate), Kota Medan, Sumatera Utara, Indonesia.
        </p>
      </Clause>
    </PageLayout>
  );
}
